import { User } from 'firebase/auth';
import { FC, useState } from 'react';

import { useUserJobs } from '../../hooks/useUserJobs';
import { deleteVideoJob } from '../../services/videoApi';
import { JobStatus, VideoJob } from '../../types';

interface JobsListProps {
  user: User;
}

const JobsList: FC<JobsListProps> = () => {
  const { jobs, loading, error } = useUserJobs();
  const [previewJob, setPreviewJob] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [deletingJobs, setDeletingJobs] = useState<Set<string>>(new Set());

  const toggleExpanded = (jobId: string) => {
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  const getStatusStyles = (status: JobStatus): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 px-2 py-1 rounded';
      case 'processing':
        return 'bg-blue-100 text-blue-800 px-2 py-1 rounded animate-pulse';
      case 'uploading':
        return 'bg-yellow-100 text-yellow-800 px-2 py-1 rounded';
      case 'failed':
        return 'bg-red-100 text-red-800 px-2 py-1 rounded';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded';
      default:
        return 'bg-gray-100 text-gray-600 px-2 py-1 rounded';
    }
  };

  const handleDownload = (job: VideoJob) => {
    if (!job.downloadUrl) return;

    // Trigger download
    const link = document.createElement('a');
    link.href = job.downloadUrl;
    link.download = `processed_${job.filename}`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreview = (job: VideoJob) => {
    if (!job.downloadUrl) return;

    setPreviewUrl(job.downloadUrl);
    setPreviewJob(job.id);
  };

  const handleDelete = async (job: VideoJob) => {
    if (!window.confirm(`Delete "${job.filename}"? This cannot be undone.`)) return;

    setDeletingJobs((prev) => new Set(prev).add(job.id));
    try {
      await deleteVideoJob(job.id);
      if (previewJob === job.id) setPreviewJob(null);
    } catch (err) {
      console.error('Failed to delete job:', err);
      alert('Failed to delete job. Please try again.');
    } finally {
      setDeletingJobs((prev) => {
        const next = new Set(prev);
        next.delete(job.id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto mt-8 p-6 card">
        <h2 className="card-header">Your Processing Jobs</h2>
        <p className="text-gray-500">Loading jobs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto mt-8 p-6 card">
        <h2 className="card-header">Your Processing Jobs</h2>
        <div className="alert-danger">
          <p className="alert-danger-text">Error loading jobs: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-8 p-6 card">
      <h2 className="card-header">Your Processing Jobs</h2>
      <div className="space-y-4">
        {jobs.length === 0 ? (
          <p className="text-muted">No processing jobs yet. Upload a video to get started!</p>
        ) : (
          jobs.map((job) => {
            const isExpanded = expandedJobs.has(job.id);
            const isDeleting = deletingJobs.has(job.id);
            return (
              <div key={job.id} className="job-item">
                {/* Collapsed row — always visible */}
                <div className="job-item-content">
                  <button
                    onClick={() => toggleExpanded(job.id)}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-transform duration-200 mr-2"
                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    ▶
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`${getStatusStyles(job.status)} text-xs font-semibold uppercase flex-shrink-0`}
                      >
                        {job.status}
                      </span>
                      <h3 className="job-title truncate">{job.filename}</h3>
                      <p className="job-subtitle flex-shrink-0">
                        {new Date(job.createdAt).toLocaleDateString()}{' '}
                        {new Date(job.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {job.status === 'completed' && job.downloadUrl && (
                      <>
                        <button
                          onClick={() => handlePreview(job)}
                          className="px-4 py-2 text-sm btn-secondary"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => handleDownload(job)}
                          className="px-4 py-2 text-sm btn-primary"
                        >
                          Download
                        </button>
                      </>
                    )}
                    {job.status === 'processing' && (
                      <div className="text-sm text-blue-600">Processing...</div>
                    )}
                    {job.status === 'uploading' && (
                      <div className="text-sm text-yellow-600">Uploading...</div>
                    )}
                    <button
                      onClick={() => handleDelete(job)}
                      disabled={isDeleting}
                      className="px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Delete job"
                    >
                      {isDeleting ? '…' : '🗑'}
                    </button>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-1">
                    <p className="job-subtitle">Stages: {job.config.stages.join(', ')}</p>
                    {job.config.targetFps && (
                      <p className="job-subtitle">
                        Target FPS: {job.config.targetFps} ({job.config.interpolationBackend})
                      </p>
                    )}
                    {job.metrics?.processingTime && (
                      <p className="job-subtitle">
                        Processing time: {job.metrics.processingTime.toFixed(1)}s
                      </p>
                    )}
                    {job.estimatedCost && (
                      <p className="job-subtitle">Cost: ${job.estimatedCost.toFixed(4)}</p>
                    )}
                    {job.error && <p className="job-error">Error: {job.error.message}</p>}
                  </div>
                )}

                {previewJob === job.id && previewUrl && (
                  <div className="job-preview-container">
                    <div className="job-preview-header">
                      <h4 className="job-preview-title">Video Preview</h4>
                      <button onClick={() => setPreviewJob(null)} className="job-preview-close">
                        ✕ Close
                      </button>
                    </div>
                    <video
                      src={previewUrl}
                      controls
                      className="w-full rounded-lg shadow-md"
                      style={{ maxHeight: '400px' }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default JobsList;
